import clsx from 'clsx'
import {memo, useCallback} from 'react'

import {makeStyles, Theme} from '../../theme'
import {isToday} from './utils'

const useDayStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    cursor: 'pointer',

    '&:empty': {
      cursor: 'default',
    },
  },
  inRange: {
    backgroundColor: theme.go.schemes.colors.system.SubtleAccent,
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '50%',
    },
    '&$firstInRange$lastInRange': {
      backgroundColor: 'transparent',
    },
  },
  firstInRange: {
    '&$selectedDay::before': {
      left: 0,
      backgroundColor: theme.go.schemes.colors.system.BgMain,
    },
  },
  lastInRange: {
    '&$selectedDay::before': {
      right: 0,
      backgroundColor: theme.go.schemes.colors.system.BgMain,
    },
  },
  today: {
    color: theme.go.schemes.colors.system.ControlMain,
  },
  dayLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    top: -2,
    bottom: -2,
    borderRadius: '50%',
    ...theme.go.mixins.effects.Hovered('$root:hover &'),
    ...theme.go.mixins.effects.Pressed('$root:hover:active &'),
    '&': {
      position: 'absolute',
      overflow: 'hidden',
    },
  },
  selectedDay: {
    '& $dayLabel': {
      color: theme.go.schemes.components.lists.selectedTextColor,
      backgroundColor: theme.go.schemes.colors.system.SelectedListItem,
    },
  },
  disabled: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
}))

export type DayProps = {
  date: string
  day: number
  onClick?: (date: string) => void
  inRange?: boolean
  firstInRange?: boolean
  lastInRange?: boolean
  classes?: {
    root?: string
  }
  selected?: boolean
  disabled?: boolean
}

export const Day = memo<DayProps>(function Day({
  date,
  day,
  onClick,
  inRange,
  firstInRange,
  lastInRange,
  classes,
  selected,
  disabled,
}: DayProps) {
  const {
    inRange: inRangeClass,
    firstInRange: firstInRangeClass,
    lastInRange: lastInRangeClass,
    disabled: disabledClass,
    today,
    dayLabel,
    root,
    selectedDay,
  } = useDayStyles()
  const dayClass = clsx(dayLabel, isToday(date) ? today : null)
  const rootClass = [root, classes?.root, selected ? selectedDay : null, disabled ? disabledClass : null]

  if (firstInRange) {
    rootClass.push(inRangeClass, firstInRangeClass)
  }
  if (lastInRange) {
    rootClass.push(inRangeClass, lastInRangeClass)
  }
  if (inRange) {
    rootClass.push(inRangeClass)
  }

  const onDayClick = useCallback(() => {
    return onClick?.(date)
  }, [date, onClick])

  return (
    <div className={clsx(...rootClass)} onClick={day ? onDayClick : undefined}>
      {day ? <span className={dayClass}>{day}</span> : null}
    </div>
  )
})
